import React, { useCallback } from 'react';
import Card from './Card';
import useCardSelector from './CardSelector';

function CardGrid({ cards, viewMode = 'grid', onEdit, onArchive, onDelete, onDuplicate, groupByCategory = null }) {
  // Set the container class based on the viewMode
  const containerClassName = `cards-container ${viewMode}-view`;
  
  // Export function for selected cards
  const handleExportSelected = useCallback((selectedCards) => {
    try {
      const dataStr = JSON.stringify(selectedCards, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const exportFileName = 'selected-cards.json';
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileName);
      linkElement.click();
      
      // Show toast notification if available
      if (window.toast) {
        window.toast.success(`${selectedCards.length} cards exported successfully`);
      }
    } catch (error) {
      if (window.toast) {
        window.toast.error('Failed to export selected cards');
      }
      console.error('Error exporting selected cards:', error);
    }
  }, []);

  // Use the cardSelector hook
  const { 
    withSelectionWrapper, 
    renderSelectionToolbar, 
    selectedCount, 
    onCardArchiveChange 
  } = useCardSelector(
    cards,
    onDelete,
    onDuplicate,
    onArchive,
    handleExportSelected
  );
  
  // Handle archive with selection tracking
  const handleArchiveWithTracking = useCallback((cardId) => {
    const result = onArchive(cardId);
    
    // If the card was successfully archived, notify the selector
    if (result !== false) {
      onCardArchiveChange(cardId);
    }
    
    return result;
  }, [onArchive, onCardArchiveChange]);

  // Group cards by category if a grouping is specified
  const groupedCards = React.useMemo(() => {
    if (!groupByCategory) {
      return [{ category: null, items: cards }]; // No grouping
    }

    // Get unique categories from cards
    const groups = {};
    
    cards.forEach(card => {
      let categoryValue = null;
      
      if (groupByCategory === 'firstLetter') {
        // Group by first letter of the title
        categoryValue = card.title.charAt(0).toUpperCase();
      } else if (groupByCategory === 'tag' && Array.isArray(card.tags) && card.tags.length > 0) {
        // Use first tag as category
        categoryValue = card.tags[0];
      } else if (card[groupByCategory]) {
        // Try to use the specified card property
        categoryValue = card[groupByCategory];
      }
      
      // Default category for cards without the specified category
      if (!categoryValue) {
        categoryValue = 'Uncategorized';
      }
      
      if (!groups[categoryValue]) {
        groups[categoryValue] = [];
      }
      
      groups[categoryValue].push(card);
    });
    
    // Convert to array and sort by category name
    return Object.entries(groups)
      .map(([category, items]) => ({ category, items }))
      .sort((a, b) => a.category.localeCompare(b.category));
      
  }, [cards, groupByCategory]);
  
  return (
    <>
      {selectedCount > 0 && renderSelectionToolbar()}
      
      <div 
        className={containerClassName}
        onContextMenu={(e) => {
          // Only prevent default context menu on the container background
          if (e.target === e.currentTarget) {
            e.preventDefault();
          }
        }}
      >
        {groupedCards.map((group, groupIndex) => (
          <React.Fragment key={group.category || 'all'}>
            {/* Render category divider if we're grouping and this isn't the first group */}
            {groupByCategory && group.category && (
              <div className="category-divider">
                <span className="category-name">{group.category}</span>
                <hr />
              </div>
            )}
            
            {/* Render all cards in this group */}
            {group.items.map((card) => 
              withSelectionWrapper(
                <Card
                  key={card.id}
                  card={card}
                  viewMode={viewMode}
                  onEdit={onEdit}
                  onArchive={handleArchiveWithTracking}
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                />
              )
            )}
          </React.Fragment>
        ))}
      </div>
    </>
  );
}

export default CardGrid;